import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  UserPlusIcon,
  CheckCircleIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  XMarkIcon,
  AcademicCapIcon,
  CreditCardIcon,
  BanknotesIcon,
  CalendarIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';

const QuickActionsFAB = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Always call hooks at the top level
  const { isOrganizationOwner, isCenterManager, isCoach, isParent, isStudent, profile } = useAuth();
  const navigate = useNavigate();

  const getQuickActions = () => {
    try {
      // If auth context is not ready yet, return empty array but don't fail
      if (!profile) {
        return [];
      }

      // If auth functions are not available, return empty array
      if (!isOrganizationOwner || !isCenterManager || !isCoach || !isParent || !isStudent) {
        return [];
      }

      // Check user roles and return appropriate actions
      if (isOrganizationOwner() || isCenterManager()) {
        return [
          {
            name: 'Create Student',
            icon: UserPlusIcon,
            action: () => navigate('/app/students'),
            color: 'bg-blue-600 hover:bg-blue-700'
          },
          {
            name: 'Mark Attendance',
            icon: CheckCircleIcon,
            action: () => navigate('/app/mark-attendance'),
            color: 'bg-green-600 hover:bg-green-700'
          },
          {
            name: 'Send Message',
            icon: ChatBubbleLeftRightIcon,
            action: () => navigate('/app/messages'),
            color: 'bg-purple-600 hover:bg-purple-700'
          },
          {
            name: 'Update Payment',
            icon: PencilSquareIcon,
            action: () => navigate('/app/payments'),
            color: 'bg-orange-600 hover:bg-orange-700'
          },
          // {
          //   name: 'Create One-off Payment',
          //   icon: BanknotesIcon,
          //   action: () => navigate('/app/one-time-payments'),
          //   color: 'bg-yellow-600 hover:bg-yellow-700'
          // },
          {
            name: 'Create Special Class',
            icon: AcademicCapIcon,
            action: () => navigate('/app/special-classes'),
            color: 'bg-indigo-600 hover:bg-indigo-700'
          },
          {
            name: 'Manage Calendar',
            icon: CalendarIcon,
            action: () => navigate('/app/upcoming-classes'),
            color: 'bg-teal-600 hover:bg-teal-700'
          }
        ];
      } else if (isCoach()) {
        return [
          {
            name: 'Create Student',
            icon: UserPlusIcon,
            action: () => navigate('/app/students'),
            color: 'bg-blue-600 hover:bg-blue-700'
          },
          {
            name: 'Mark Attendance',
            icon: CheckCircleIcon,
            action: () => navigate('/app/mark-attendance'),
            color: 'bg-green-600 hover:bg-green-700'
          },
          {
            name: 'Send Message',
            icon: ChatBubbleLeftRightIcon,
            action: () => navigate('/app/messages'),
            color: 'bg-purple-600 hover:bg-purple-700'
          },
          {
            name: 'Manage Calendar',
            icon: CalendarIcon,
            action: () => navigate('/app/upcoming-classes'),
            color: 'bg-teal-600 hover:bg-teal-700'
          }
        ];
      } else if (isParent()) {
        return [
          {
            name: 'View Progress',
            icon: DocumentTextIcon,
            action: () => navigate('/app/student-progress'),
            color: 'bg-green-600 hover:bg-green-700'
          },
          {
            name: 'Pay Bills',
            icon: CreditCardIcon,
            action: () => navigate('/app/payments'),
            color: 'bg-blue-600 hover:bg-blue-700'
          },
          {
            name: 'Send Message',
            icon: ChatBubbleLeftRightIcon,
            action: () => navigate('/app/messages'),
            color: 'bg-purple-600 hover:bg-purple-700'
          },
          {
            name: 'Schedule',
            icon: CalendarDaysIcon,
            action: () => navigate('/app/upcoming-classes'),
            color: 'bg-orange-600 hover:bg-orange-700'
          }
        ];
      } else if (isStudent()) {
        return [
          {
            name: 'My Schedule',
            icon: CalendarDaysIcon,
            action: () => navigate('/app/upcoming-classes'),
            color: 'bg-blue-600 hover:bg-blue-700'
          },
          {
            name: 'My Progress',
            icon: DocumentTextIcon,
            action: () => navigate('/app/student-progress'),
            color: 'bg-green-600 hover:bg-green-700'
          },
          {
            name: 'Send Message',
            icon: ChatBubbleLeftRightIcon,
            action: () => navigate('/app/messages'),
            color: 'bg-purple-600 hover:bg-purple-700'
          }
        ];
      }
      return [];
    } catch (error) {
      // Silent error handling - don't log to console
      return [];
    }
  };

  try {
    const quickActions = getQuickActions();

    // Always show the FAB button, even if no actions are available yet
    // This ensures the button appears on all pages while auth context loads

    const handleAction = (actionFn) => {
      try {
        actionFn();
        setIsOpen(false);
      } catch (error) {
        // Silent error handling - don't log to console
      }
    };

    return (
      <div className="fixed bottom-20 right-6 z-[60]">
        {/* Backdrop to close when clicking outside - must be first */}
        {isOpen && (
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-20"
            onClick={() => setIsOpen(false)}
          />
        )}

        {/* Action buttons */}
        {isOpen && quickActions.length > 0 && (
          <div className="mb-4 space-y-3 relative z-[70]">
            {quickActions.map((action, index) => (
              <div
                key={action.name}
                className={`transform transition-all duration-300 ease-out relative z-[70] ${isOpen
                  ? 'translate-y-0 opacity-100 scale-100'
                  : 'translate-y-4 opacity-0 scale-95'
                  }`}
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                <button
                  onClick={(e) => {
                    handleAction(action.action);
                  }}
                  className={`group flex items-center space-x-3 ${action.color} text-white px-4 py-3 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 relative z-[70] whitespace-nowrap`}
                  title={action.name}
                >
                  <action.icon className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm font-medium">{action.name}</span>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Main FAB button */}
        <button
          onClick={() => {
            setIsOpen(!isOpen);
          }}
          className={`w-14 h-14 ${isOpen ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
            } text-white rounded-full shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-200 flex items-center justify-center relative z-[70]`}
          aria-label={isOpen ? 'Close quick actions' : 'Open quick actions'}
        >
          {isOpen ? (
            <XMarkIcon className="h-6 w-6 transform rotate-0 transition-transform duration-200" />
          ) : (
            <PlusIcon className="h-6 w-6 transform rotate-0 transition-transform duration-200" />
          )}
        </button>
      </div>
    );
  } catch (error) {
    // Silent error handling - don't log to console
    return null; // Don't render if there's an error
  }
};

export default QuickActionsFAB;